"use client";
import React, { useState } from "react";
import StarIcon from "@/assets/icons/star.svg";
import StarHalfIcon from "@/assets/icons/star-half.svg";
import { twMerge } from "tailwind-merge";
import { empty } from "@/shared/helpers";

export interface Props {
  rating: number;
  onChange?: (ix: number) => void;
  locked?: boolean;
  starClassName?: string;
  useHalfStars?: boolean;
}

const Rating = ({
  rating,
  locked,
  starClassName,
  useHalfStars,
  onChange = empty,
}: Props) => {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [currentRating, setCurrentRating] = useState(rating);

  const isInteractive = !locked && !useHalfStars;

  const handleMouseEnter = (index: number) => {
    if (isInteractive) {
      setHoveredRating(index);
    }
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      setHoveredRating(null);
    }
  };

  const handleClick = (index: number) => {
    if (isInteractive) {
      setCurrentRating(index);
    }
    onChange(index);
  };

  const getStarFill = (index: number) => {
    if (hoveredRating !== null && !useHalfStars) {
      return index <= hoveredRating ? "gold" : "grey";
    } else {
      return index <= currentRating ? "gold" : "grey";
    }
  };

  const renderStars = () => {
    if (!useHalfStars || !locked) {
      return [1, 2, 3, 4, 5].map((index) => (
        <StarIcon
          key={index}
          className={twMerge("w-6", starClassName)}
          fill={getStarFill(index)}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
          onClick={() => handleClick(index)}
          style={{ cursor: locked ? "default" : "pointer" }}
        />
      ));
    } else {
      return [1, 2, 3, 4, 5].map((index) => {
        const fullStars = Math.floor(currentRating);
        const fractionalPart = currentRating - fullStars;
        const dynamicStar =
          fractionalPart < 0.25 ? (
            <StarIcon
              key={index}
              className={twMerge("w-6", starClassName)}
              fill="grey"
            />
          ) : fractionalPart < 0.75 ? (
            <StarHalfIcon
              key={index}
              className={twMerge("w-6", starClassName)}
              fill="gold"
            />
          ) : (
            <StarIcon
              key={index}
              className={twMerge("w-6", starClassName)}
              fill="gold"
            />
          );

        if (index <= fullStars) {
          return (
            <StarIcon
              key={index}
              className={twMerge("w-6", starClassName)}
              fill="gold"
            />
          );
        } else if (index === fullStars + 1) {
          return dynamicStar;
        } else {
          return (
            <StarIcon
              key={index}
              className={twMerge("w-6", starClassName)}
              fill="grey"
            />
          );
        }
      });
    }
  };

  return <div style={{ display: "flex" }}>{renderStars()}</div>;
};

export default Rating;
